import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Users, GraduationCap, Building2, Landmark, LifeBuoy, Settings, LogOut, ScrollText, UserCircle, BookOpen } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { PERMISSIONS, hasAnyPermission, hasPermission } from '../../constants/permissions';

export default function AdminSidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isInstitutionAdmin = user?.role === 'institution_admin';
  const isClusterAdmin = user?.role === 'cluster_admin';
  const navItems = isInstitutionAdmin
    ? [
        { name: 'Dashboard', path: '/institution', icon: <LayoutDashboard size={20} />, permission: PERMISSIONS.DASHBOARD_VIEW },
        { name: 'Admissions', path: '/institution/admissions', icon: <GraduationCap size={20} />, permission: PERMISSIONS.ADMISSIONS_VIEW },
        { name: 'Courses', path: '/institution/courses', icon: <Building2 size={20} />, permission: PERMISSIONS.COURSES_VIEW },
        { name: 'Course Management', path: '/institution/course-management', icon: <BookOpen size={20} />, permission: PERMISSIONS.COURSE_MANAGEMENT_VIEW },
        { name: 'Students', path: '/institution/students', icon: <Users size={20} />, permission: PERMISSIONS.STUDENTS_VIEW },
        { name: 'ERP Platform', path: '/institution/erp', icon: <Settings size={20} />, permission: PERMISSIONS.COURSES_VIEW },
        { name: 'Settlements', path: '/institution/settlements', icon: <Landmark size={20} />, permission: PERMISSIONS.SETTLEMENTS_VIEW },
        { name: 'Logs', path: '/institution/logs', icon: <ScrollText size={20} />, permission: PERMISSIONS.LOGS_VIEW },
        { name: 'User Management', path: '/institution/user-management', icon: <Users size={20} />, permission: [PERMISSIONS.USER_MANAGEMENT_VIEW, PERMISSIONS.ADMINS_MANAGE] },
        { name: 'My Profile', path: '/institution/profile', icon: <UserCircle size={20} /> },
      ]
    : isClusterAdmin
    ? [
        { name: 'Dashboard', path: '/cluster-admin', icon: <LayoutDashboard size={20} />, permission: PERMISSIONS.DASHBOARD_VIEW },
        { name: 'Admissions', path: '/cluster-admin/admissions', icon: <GraduationCap size={20} />, permission: PERMISSIONS.ADMISSIONS_VIEW },
        { name: 'Institutions', path: '/cluster-admin/colleges', icon: <Building2 size={20} />, permission: PERMISSIONS.INSTITUTIONS_VIEW },
        { name: 'Course Management', path: '/cluster-admin/course-management', icon: <BookOpen size={20} />, permission: PERMISSIONS.COURSE_MANAGEMENT_VIEW },
        { name: 'Students', path: '/cluster-admin/students', icon: <Users size={20} />, permission: PERMISSIONS.STUDENTS_VIEW },
        { name: 'Settlements', path: '/cluster-admin/settlements', icon: <Landmark size={20} />, permission: PERMISSIONS.SETTLEMENTS_VIEW },
        { name: 'Logs', path: '/cluster-admin/logs', icon: <ScrollText size={20} />, permission: PERMISSIONS.LOGS_VIEW },
        { name: 'User Management', path: '/cluster-admin/user-management', icon: <Users size={20} />, permission: [PERMISSIONS.USER_MANAGEMENT_VIEW, PERMISSIONS.ADMINS_MANAGE] },
        { name: 'Settings', path: '/cluster-admin/settings', icon: <Settings size={20} />, permission: PERMISSIONS.SETTINGS_VIEW },
        { name: 'My Profile', path: '/cluster-admin/profile', icon: <UserCircle size={20} /> },
      ]
    : [
        { name: 'Dashboard', path: '/admin', icon: <LayoutDashboard size={20} />, permission: PERMISSIONS.DASHBOARD_VIEW },
        { name: 'Admissions', path: '/admin/admissions', icon: <GraduationCap size={20} />, permission: PERMISSIONS.ADMISSIONS_VIEW },
        { name: 'Institutions', path: '/admin/colleges', icon: <Building2 size={20} />, permission: PERMISSIONS.INSTITUTIONS_VIEW },
        { name: 'Students', path: '/admin/students', icon: <Users size={20} />, permission: PERMISSIONS.STUDENTS_VIEW },
        { name: 'Settlements', path: '/admin/settlements', icon: <Landmark size={20} />, permission: PERMISSIONS.SETTLEMENTS_VIEW },
        { name: 'Support Desk', path: '/admin/support', icon: <LifeBuoy size={20} />, permission: PERMISSIONS.SUPPORT_VIEW },
        { name: 'Logs', path: '/admin/logs', icon: <ScrollText size={20} />, permission: PERMISSIONS.LOGS_VIEW },
        { name: 'User Management', path: '/admin/user-management', icon: <Users size={20} />, permission: [PERMISSIONS.USER_MANAGEMENT_VIEW, PERMISSIONS.ADMINS_MANAGE] },
        { name: 'Settings', path: '/admin/settings', icon: <Settings size={20} />, permission: PERMISSIONS.SETTINGS_VIEW },
        { name: 'My Profile', path: '/admin/profile', icon: <UserCircle size={20} /> },
      ];
  const visibleNavItems = navItems.filter((item) => (
    !item.permission || (Array.isArray(item.permission) ? hasAnyPermission(user, item.permission) : hasPermission(user, item.permission))
  ));

  return (
    <aside className="w-64 h-screen bg-slate-900 text-slate-300 flex flex-col fixed left-0 top-0">
      <div className="p-6 pt-2 border-b border-slate-800">
        <div className="flex items-center">
          <img src="/white-logo.webp" alt="Seatify Admin" style={{ height: '70px', width: 'auto' }} />
        </div>
        <p className="text-xs text-slate-500 mt-1 uppercase tracking-widest font-bold">Control Panel</p>
      </div>

      <nav className="flex-1 p-4 space-y-2 overflow-y-auto sidebar-scrollbar">
        {visibleNavItems.map(item => (
          <NavLink
            key={item.name}
            to={item.path}
            end={item.path === '/admin' || item.path === '/cluster-admin' || item.path === '/institution'}
            className={({ isActive }) => 
              `flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all ${
                isActive 
                  ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/30' 
                  : 'hover:bg-slate-800 hover:text-white'
              }`
            }
          >
            {item.icon}
            {item.name}
          </NavLink>
        ))}
      </nav>

      <div className="p-4 border-t border-slate-800">
        <div className="flex items-center gap-3 mb-4 px-2">
          <div className="w-10 h-10 rounded-full bg-indigo-500 text-white flex items-center justify-center font-bold text-lg">
            {user?.name?.[0]?.toUpperCase() || 'A'}
          </div>
          <div className="overflow-hidden">
            <p className="text-sm font-bold text-white truncate">{user?.name}</p>
            <p className="text-xs text-slate-400 truncate">{user?.email || user?.mobile}</p>
          </div>
        </div>
        <button 
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-rose-400 hover:bg-rose-500/10 hover:text-rose-300 transition-all font-medium"
        >
          <LogOut size={20} />
          Logout
        </button>
      </div>
    </aside>
  );
}
